import ShipmentService from "./shipment"

const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000 // 7 days
const CITY_DATA_CACHE: { [key: string]: any } = {} // Persistent cache for city data
const STATE_DATA_CACHE: { [key: string]: any } = {} // Persistent cache for state data
const TRANSPORTER_CACHE: { [key: string]: any } = {} // Persistent cache for transporters
const FAILED_REQUESTS: { [key: string]: number } = {} // Track any failed requests
const RATE_LIMIT_DURATION = 60 * 1000 // 1 minute - shorter than the crawler's interval

interface CacheEntry<T> {
  data: T
  timestamp: number
}

const REFRESH_THRESHOLD = 6 * 24 * 60 * 60 * 1000 // 6 days - trigger refresh when near expiration

const cacheStore: { [key: string]: CacheEntry<any> } = {}

export const getCachedData = async <T>(
  key: string,
  fetchFn: () => Promise<T>,
): Promise<T> => {
  const cached = cacheStore[key]
  const now = Date.now()

  try {
    if (cached && now - cached.timestamp < CACHE_DURATION) {
      if (now - cached.timestamp > REFRESH_THRESHOLD) {
        refreshCache(key, fetchFn).catch(console.error)
      }
      return cached.data
    }

    const data = await fetchFn()
    if (
      !data ||
      (typeof data === "object" && Object.keys(data).length === 0) ||
      (Array.isArray(data) && data.length === 0)
    ) {
      if (cached?.data) {
        return cached.data
      }
      return {} as T
    }
    cacheStore[key] = { data, timestamp: now }
    return data
  } catch (error: any) {
    if (error?.response?.status === 429 && cached?.data) {
      return cached.data
    }
    if (error?.response?.status === 429) {
      return {} as T
    }
    if (cached?.data) {
      return cached.data
    }
    return {} as T
  }
}

async function refreshCache<T>(key: string, fetchFn: () => Promise<T>) {
  try {
    const data = await fetchFn()
    cacheStore[key] = { data, timestamp: Date.now() }
  } catch (error) {
    console.error(`Background refresh failed:`, error)
  }
}

// Add exponential backoff retry helper
export const withRetry = async <T>(
  fn: () => Promise<T>,
  retries = 3,
  baseDelay = 1000,
): Promise<T> => {
  for (let i = 0; i < retries; i++) {
    try {
      return await fn()
    } catch (error: any) {
      if (error?.response?.status === 429) {
        if (i === retries - 1) {
          throw error
        }
        const delay = baseDelay * Math.pow(2, retries - i - 1)
        await new Promise((resolve) => setTimeout(resolve, delay))
        continue
      }
      throw error
    }
  }
  throw new Error("Max retries reached")
}

export const fetchWithRetry = withRetry

export const getCityShipmentData = async (
  city: string,
  stateAbbrev: string,
) => {
  const cacheKey = `${stateAbbrev}:${city}`.toLowerCase()

  const failedTimestamp = FAILED_REQUESTS[cacheKey]
  const isRateLimited =
    failedTimestamp && Date.now() - failedTimestamp < RATE_LIMIT_DURATION

  // Check persistent cache first
  if (
    CITY_DATA_CACHE[cacheKey]?.exportShipments?.length > 0 &&
    CITY_DATA_CACHE[cacheKey]?.importShipments?.length > 0 &&
    !isRateLimited
  ) {
    return CITY_DATA_CACHE[cacheKey]
  }

  try {
    const [exportData, importData] = await Promise.all([
      withRetry(() => ShipmentService.getCityExportData(city, stateAbbrev)),
      withRetry(() => ShipmentService.getCityImportData(city, stateAbbrev)),
    ])

    const data = {
      exportShipments: formatShipmentData(exportData?.data?.data),
      importShipments: formatShipmentData(importData?.data?.data),
    }

    if (data.exportShipments?.length && data.importShipments?.length) {
      CITY_DATA_CACHE[cacheKey] = data
      delete FAILED_REQUESTS[cacheKey]
    } else {
      FAILED_REQUESTS[cacheKey] = Date.now()
    }

    return data
  } catch (error) {
    console.error(`Failed to fetch data for ${cacheKey}:`, error)
    if ((error as ApiError)?.response) {
      FAILED_REQUESTS[cacheKey] = Date.now()
    }
    return (
      CITY_DATA_CACHE[cacheKey] || { exportShipments: [], importShipments: [] }
    )
  }
}

export const getStateShipmentData = async (state: string) => {
  const cacheKey = state.toLowerCase()

  const failedTimestamp = FAILED_REQUESTS[cacheKey]
  const isRateLimited =
    failedTimestamp && Date.now() - failedTimestamp < RATE_LIMIT_DURATION

  // Check persistent cache first
  if (
    (STATE_DATA_CACHE[cacheKey]?.exportShipments?.length ||
      STATE_DATA_CACHE[cacheKey]?.importShipments?.length) &&
    !isRateLimited
  ) {
    return STATE_DATA_CACHE[cacheKey]
  }

  try {
    const [exportData, importData] = await Promise.all([
      withRetry(() => ShipmentService.getStateExportData(state)),
      withRetry(() => ShipmentService.getStateImportData(state)),
    ])

    const data = {
      exportShipments: formatShipmentData(exportData?.data?.data),
      importShipments: formatShipmentData(importData?.data?.data),
    }

    if (data.exportShipments?.length && data.importShipments?.length) {
      STATE_DATA_CACHE[cacheKey] = data
      delete FAILED_REQUESTS[cacheKey]
    } else {
      FAILED_REQUESTS[cacheKey] = Date.now()
    }

    return data
  } catch (error) {
    console.error("Failed to fetch state shipment data:", { state, error })
    if ((error as ApiError)?.response) {
      FAILED_REQUESTS[cacheKey] = Date.now()
    }
    return (
      STATE_DATA_CACHE[cacheKey] || { exportShipments: [], importShipments: [] }
    )
  }
}

interface ApiError extends Error {
  response?: {
    status: number
    data: {
      data?: any
    }
  }
}

export const getTransportersData = async (
  type: "state" | "city",
  city: string,
  stateAbbrev?: string,
) => {
  const cacheKey =
    type === "state"
      ? `state:${city}`.toLowerCase()
      : `city:${city}:${stateAbbrev}`.toLowerCase()

  // Check persistent cache first
  const failedTimestamp = FAILED_REQUESTS[cacheKey]
  const isRateLimited =
    failedTimestamp && Date.now() - failedTimestamp < RATE_LIMIT_DURATION

  if (TRANSPORTER_CACHE[cacheKey]?.length > 0 && !isRateLimited) {
    return TRANSPORTER_CACHE[cacheKey]
  }

  try {
    const response =
      type === "state"
        ? await withRetry(() => ShipmentService.getStateTransporters(city))
        : await withRetry(() =>
            ShipmentService.getCityTransporters(city, stateAbbrev),
          )

    const transporters = response?.data?.data || []
    const data = transporters.filter(Boolean).map((t: any) => ({
      ...t,
      userId: t.userId || 0,
      pickupCity: t.pickupCity || "",
      pickupStateCode: t.pickupStateCode || "",
      dba: t.dba || "",
      vehicleType: t.vehicleType || "",
      avgRating: t.avgRating || "0",
      photo: t.photo || null,
    }))

    // Only cache if we got valid data
    if (data.length > 0) {
      TRANSPORTER_CACHE[cacheKey] = data
      delete FAILED_REQUESTS[cacheKey]
    } else {
      // Mark as failed if we got empty data even with 200 response
      FAILED_REQUESTS[cacheKey] = Date.now()
    }

    return data
  } catch (error: unknown) {
    console.error("Failed to fetch transporters data:", {
      type,
      city,
      error,
    })
    // Mark as failed for any error response
    if ((error as ApiError)?.response) {
      FAILED_REQUESTS[cacheKey] = Date.now()
    }
    return TRANSPORTER_CACHE[cacheKey] || []
  }
}

export const formatShipmentData = (data: any[] = []) => {
  return data?.map((item: any) => ({
    ...item,
    breed: item.petBreed,
    animalName: item.petName,
    pickupStateCode: item.pickupStateCode,
    pickupZipCode: item.pickupZipCode,
    tags: [item.petsize, parseFloat(item.distance)],
    customer: {
      image: null,
      name: item.driverName,
      rating: item.rating,
    },
    subType: item.subCategory,
    deliverCity: item.deliveryCity,
    deliverStateCode: item.deliveryStateCode,
    photo: item.profilePhotoUrl,
  }))
}

export default cacheStore
